// Admin reference for the eBay Sell Communications Message API (commerce/message/v1):
// every method, parameter and response field, whether CXHub pulls it, and the
// exact DB column it lands in. Scoped to the Message API only.
//
// Hand-maintained — the "Pulling? / DB column" rows mirror the channel code
// (client.js · mapper.js · ingest.js) and migrations 0001/0006, so they ship
// with the release that changes them. The endpoint STATUS chips are bound to the
// LIVE read/write flags (GET /api/ebay/flags) so they reflect reality the moment
// writes are enabled. The "Last reviewed" pill counts days since LAST_REVIEWED and
// nudges to re-check eBay's docs once it's stale — bump the constant after a check.

import { useEffect, useState } from 'react'
import {
  Box, Typography, Stack, Chip, Divider, Link, Paper, Alert, Button, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material'
import { fetchEbayFlags, fetchEbayRawShape } from './api.js'

// Bump this whenever you've checked the field list against eBay's published docs.
const LAST_REVIEWED = '2026-06-26'
const STALE_DAYS = 90

const STATUS = {
  pull:   { label: 'Pulling',             color: 'success' },
  avail:  { label: 'Available · not used', color: 'default' },
  verify: { label: 'Documented · to verify', color: 'info' },
  gate:   { label: 'Built · gated off',   color: 'warning' },
  avoid:  { label: 'Avoided by design',   color: 'error' },
}
const StatusChip = ({ s }) => (
  <Chip size="small" label={STATUS[s].label} color={STATUS[s].color} variant={s === 'avail' ? 'outlined' : 'filled'} />
)

const Mono = ({ children, dim }) => (
  <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12.5, color: dim ? 'text.disabled' : 'text.primary', fontStyle: dim ? 'italic' : 'normal' }}>
    {children}
  </Box>
)
const Yes = () => <Box component="span" sx={{ fontWeight: 700, color: 'success.main' }}>Yes</Box>
const No = () => <Box component="span" sx={{ fontWeight: 700, color: 'text.disabled' }}>No</Box>
const Badge = ({ s }) => <Chip size="small" label={STATUS[s].label} color={STATUS[s].color} variant="outlined" sx={{ ml: 1, height: 18, fontSize: 10.5 }} />

// --- Request-parameter table -------------------------------------------------
function ParamTable({ rows }) {
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ mb: 2, minWidth: 560 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Parameter</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Required</TableCell>
            <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Sending?</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Value we send</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.p}>
              <TableCell sx={{ verticalAlign: 'top' }}><Mono>{r.p}</Mono></TableCell>
              <TableCell sx={{ verticalAlign: 'top', color: r.req === 'required' ? 'warning.main' : 'text.secondary', fontSize: 12, whiteSpace: 'nowrap' }}>{r.req}</TableCell>
              <TableCell sx={{ verticalAlign: 'top', textAlign: 'center' }}>{r.send === 'y' ? <Yes /> : <Box component="span" sx={{ fontWeight: 700, color: 'text.disabled' }}>Gated</Box>}</TableCell>
              <TableCell sx={{ verticalAlign: 'top', color: 'text.secondary', fontSize: 13 }}>{r.val}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

// --- Response-field table ----------------------------------------------------
function FieldTable({ rows, pullLabel = 'Pulling?' }) {
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ mb: 2, minWidth: 620 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Field</TableCell>
            <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>{pullLabel}</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>DB column</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.f}>
              <TableCell sx={{ verticalAlign: 'top' }}><Mono>{r.f}</Mono></TableCell>
              <TableCell sx={{ verticalAlign: 'top', textAlign: 'center' }}>{r.pull ? <Yes /> : <No />}</TableCell>
              <TableCell sx={{ verticalAlign: 'top' }}>
                {r.db ? <Mono>{r.db}</Mono> : <Mono dim>— not stored</Mono>}
              </TableCell>
              <TableCell sx={{ verticalAlign: 'top', color: 'text.secondary', fontSize: 13 }}>
                {r.note}{r.badge && <Badge s={r.badge} />}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

function EndpointHead({ verb, path, fn, children }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Stack direction="row" spacing={1.2} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip size="small" label={verb} sx={{ fontFamily: 'monospace', fontWeight: 700, bgcolor: verb === 'POST' ? 'warning.light' : 'primary.light', color: verb === 'POST' ? 'warning.contrastText' : 'primary.contrastText' }} />
        <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 14 }}>{path}</Box>
        <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.disabled' }}>{fn}</Box>
        {children}
      </Stack>
    </Box>
  )
}

// ---- Field data (mirrors mapper.js + migrations 0001 / 0006) ----------------

const GETCONVS_REQ = [
  { p: 'conversation_type', req: 'required', send: 'y', val: 'Both: FROM_MEMBERS and FROM_EBAY' },
  { p: 'limit', req: 'optional', send: 'y', val: '"Load messages" count (default 10, max 50)' },
  { p: 'offset', req: 'optional', send: 'y', val: '0 — single page today; paging available for later' },
]
const GETCONVS_RES = [
  { f: 'conversations[]', pull: true, db: '→ conversations (rows)', note: 'Iterated; each becomes a conversation' },
  { f: 'conversationId', pull: true, db: 'conversations.external_conversation_id', note: 'Thread key + idempotency' },
  { f: 'conversationType', pull: true, db: 'conversations.metadata.ebayConversationType', note: 'Drives display + sanitize choice' },
  { f: 'referenceType', pull: true, db: 'conversations.reference_type', note: 'LISTING / ORDER / RETURN / …' },
  { f: 'referenceId', pull: true, db: 'conversations.reference_id', note: 'Listing / order / return id' },
  { f: 'latestMessage{}', pull: true, db: 'messages (fallback)', note: 'Recency sort key; used if the thread fetch fails' },
  { f: 'total', pull: false, db: null, note: 'Paging — total matching conversations', badge: 'avail' },
  { f: 'limit / offset (echo)', pull: false, db: null, note: 'Paging — echoes our request', badge: 'avail' },
  { f: 'next / prev', pull: false, db: null, note: 'Paging cursors (next/prev page URLs)', badge: 'avail' },
  { f: 'href', pull: false, db: null, note: 'Self link', badge: 'avail' },
  { f: 'conversation-level status / counts / timestamps', pull: false, db: null, note: 'e.g. status, unread count, created date — exact names', badge: 'verify' },
]

const GETCONV_REQ = [
  { p: 'conversationId (path)', req: 'required', send: 'y', val: 'From the summary' },
  { p: 'conversation_type', req: 'required', send: 'y', val: "Carried from the summary's type" },
]
const GETCONV_CONV = [
  { f: 'conversationId', pull: true, db: 'conversations.external_conversation_id', note: 'Idempotent upsert key' },
  { f: 'conversationType', pull: true, db: 'conversations.metadata.ebayConversationType', note: '' },
  { f: 'referenceType', pull: true, db: 'conversations.reference_type', note: '' },
  { f: 'referenceId', pull: true, db: 'conversations.reference_id', note: '' },
  { f: 'messages[]', pull: true, db: '→ messages (rows)', note: 'Iterated oldest-first' },
]
const GETCONV_MSG = [
  { f: 'messageId', pull: true, db: 'messages.external_message_id', note: 'Dedup key' },
  { f: 'messageBody', pull: true, db: 'messages.body + messages.body_sanitized', note: 'Raw kept verbatim; sanitized copy for the AI' },
  { f: 'senderUsername', pull: true, db: 'messages.metadata.senderUsername', note: 'Also derives direction + participant' },
  { f: 'recipientUsername', pull: true, db: 'messages.metadata.recipientUsername', note: '' },
  { f: 'readStatus', pull: true, db: 'messages.metadata.readStatus', note: 'Read only — never written back to eBay' },
  { f: 'createdDate', pull: true, db: 'messages.received_at', note: '' },
  { f: 'attachments[] / message-type fields', pull: false, db: null, note: 'eBay messages can carry attachments/images — presence + exact names', badge: 'verify' },
]

const SEND_BODY = [
  { p: 'messageText', req: 'required', send: 'gated', val: 'The reply body' },
  { p: 'conversationId', req: 'one of', send: 'gated', val: 'Reply into an existing thread' },
  { p: 'conversationType', req: 'optional', send: 'gated', val: 'Paired with conversationId' },
  { p: 'otherPartyUsername', req: 'one of', send: 'gated', val: 'Start a brand-new thread' },
]

const DERIVED = [
  { f: 'subject', pull: true, db: 'conversations.subject', note: 'First inbound body, sanitized + truncated (no title field exists)' },
  { f: 'direction', pull: true, db: 'messages.direction', note: 'senderUsername === seller → outbound, else inbound' },
  { f: 'participant type', pull: true, db: 'participants.type', note: 'seller → agent, "ebay" → system, else customer' },
  { f: 'buyer identity', pull: true, db: 'participants.external_user_id / display_name', note: 'From the first inbound sender' },
]

// Render the PII-safe shape object (field names + types, no values) as an indented tree.
function ShapeTree({ shape, depth = 0 }) {
  const pad = { pl: depth * 2 }
  if (shape == null) return <Box sx={{ ...pad, fontFamily: 'monospace', fontSize: 12, color: 'text.disabled' }}>null</Box>
  if (typeof shape === 'string') return <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12, color: 'info.main' }}>{shape}</Box>
  if (shape.array) {
    return (
      <Box sx={pad}>
        <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' }}>[ ] array of</Box>
        <Box sx={{ pl: 2 }}><ShapeTree shape={shape.items} depth={0} /></Box>
      </Box>
    )
  }
  if (shape.object) {
    return (
      <Box sx={pad}>
        {Object.entries(shape.fields).map(([k, v]) => (
          <Box key={k} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}>{k}:</Box>
            <ShapeTree shape={v} depth={0} />
          </Box>
        ))}
      </Box>
    )
  }
  return null
}

export default function EbayApiReference() {
  const [flags, setFlags] = useState(null)
  useEffect(() => { fetchEbayFlags().then(setFlags) }, [])

  // Admin-only, PII-safe live field-shape capture (confirms the "to verify" rows).
  const [shape, setShape] = useState(null)
  const [shapeLoading, setShapeLoading] = useState(false)
  const [shapeError, setShapeError] = useState(null)
  const captureShape = () => {
    setShapeLoading(true); setShapeError(null)
    fetchEbayRawShape(5)
      .then(setShape)
      .catch((e) => setShapeError(e.message))
      .finally(() => setShapeLoading(false))
  }

  // Optimistic defaults until the flags load (reads on, writes off — the live state).
  const readOn = flags ? flags.read : true
  const writeOn = flags ? flags.write : false

  // Read endpoints reflect the read flag; send reflects the write flag.
  const readChip = readOn
    ? <StatusChip s="pull" />
    : <Chip size="small" color="warning" label="Reads disabled" />
  const sendChip = writeOn
    ? <Chip size="small" color="success" label="Sending · live" />
    : <StatusChip s="gate" />
  const markReadOn = flags ? flags.markRead : false
  const markReadChip = markReadOn
    ? <Chip size="small" color="success" label="Marking read · live" />
    : <StatusChip s="gate" />

  // Freshness of the last manual review against eBay's docs.
  const days = Math.max(0, Math.floor((Date.now() - new Date(`${LAST_REVIEWED}T00:00:00`).getTime()) / 86400000))
  const stale = days >= STALE_DAYS
  const ago = days === 0 ? 'today' : days === 1 ? '1 day ago' : `${days} days ago`
  const reviewedLabel = new Date(`${LAST_REVIEWED}T00:00:00`).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })

  return (
    <Box sx={{ flex: 1, p: 3, overflowY: 'auto' }}>
      <Typography variant="h6" gutterBottom>eBay Message API — coverage matrix</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: '80ch' }}>
        Every method, parameter and response field of the Sell Communications Message API (<code>commerce/message/v1</code>) —
        whether CXHub pulls it, and the exact database column it lands in. Scoped to the <b>Message API only</b>.{' '}
        <Link href="https://developer.ebay.com/develop/guides-v2/sell-communications-guide" target="_blank" rel="noopener">eBay docs ↗</Link>
      </Typography>

      {/* Last-reviewed stamp + freshness pill */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">🗓 Last reviewed against eBay&apos;s API</Typography>
        <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{reviewedLabel}</Box>
        <Box sx={{ flexGrow: 1 }} />
        <Chip
          size="small"
          color={stale ? 'warning' : 'success'}
          label={`${stale ? 'Review due' : 'Current'} · ${ago}`}
        />
      </Paper>

      {/* Legend */}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {Object.keys(STATUS).map((k) => <StatusChip key={k} s={k} />)}
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        Fields are split into <b>confirmed</b> (seen in our live responses / read by our code) and{' '}
        <b>documented · to verify</b> (standard eBay fields not yet confirmed against a live response). To-verify rows are
        deliberately conservative rather than padded with invented field names — use the capture below to lock them to ground truth.
      </Alert>

      {/* Admin: PII-safe live field-shape capture */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 320px' }}>
            <Typography variant="subtitle2">Confirm fields against a live response</Typography>
            <Typography variant="caption" color="text.secondary">
              Samples a few live conversations of each type and returns the response <b>shape only</b> — field names + value
              types, never any values (PII-safe). Use it to promote “to verify” rows to confirmed.
            </Typography>
          </Box>
          <Button variant="outlined" size="small" onClick={captureShape} disabled={shapeLoading}>
            {shapeLoading ? <CircularProgress size={18} /> : 'Capture live field shape'}
          </Button>
        </Box>
        {shapeError && <Alert severity="warning" sx={{ mt: 2 }}>{shapeError}</Alert>}
        {shape && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Captured {shape.capturedAt}. {shape.note}
            </Typography>
            {Object.entries(shape.types ?? {}).map(([type, data]) => (
              <Box key={type} sx={{ mt: 1.5 }}>
                <Typography variant="overline" color="primary">{type} {data.error ? '· error' : `· sampled ${data.sampled}`}</Typography>
                {data.error ? (
                  <Typography variant="body2" color="error">{data.error}</Typography>
                ) : (
                  <Box sx={{ overflowX: 'auto', bgcolor: 'action.hover', borderRadius: 1, p: 1.5, mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>getConversations response</Typography>
                    <ShapeTree shape={data.listResponseShape} />
                    {data.conversationShape && (
                      <>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 0.5 }}>getConversation thread</Typography>
                        <ShapeTree shape={data.conversationShape} />
                      </>
                    )}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
        <Typography variant="subtitle2" gutterBottom>Auth &amp; scope</Typography>
        <Typography variant="body2" color="text.secondary">
          OAuth user token (authorization-code → refresh-token grant), scope{' '}
          <code>commerce.message</code>, seller account <code>1aauto</code>, marketplace{' '}
          <code>{flags?.marketplace ?? 'EBAY_US'}</code>{flags?.env ? <> · env <code>{flags.env}</code></> : null}.{' '}
          Reads gated by <code>EBAY_READ_ENABLED</code> (<b>{readOn ? 'on' : 'off'}</b>), sends by{' '}
          <code>EBAY_WRITE_ENABLED</code> (<b>{writeOn ? 'on' : 'off'}</b>). Status chips below reflect these live flags.
        </Typography>
      </Paper>

      {/* getConversations */}
      <EndpointHead verb="GET" path="/conversation" fn="getConversations()">{readChip}</EndpointHead>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: '80ch' }}>
        Lists conversation summaries for one type. We call it once per type and merge, newest-first, capping at the requested Count.
      </Typography>
      <Typography variant="overline" color="primary">Request parameters</Typography>
      <ParamTable rows={GETCONVS_REQ} />
      <Typography variant="overline" color="primary">Response fields</Typography>
      <FieldTable rows={GETCONVS_RES} />

      <Divider sx={{ my: 2 }} />

      {/* getConversation */}
      <EndpointHead verb="GET" path="/conversation/{id}" fn="getConversation()">{readChip}</EndpointHead>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: '80ch' }}>
        Full message thread for one conversation — both directions. This is where every message row comes from.
      </Typography>
      <Typography variant="overline" color="primary">Request parameters</Typography>
      <ParamTable rows={GETCONV_REQ} />
      <Typography variant="overline" color="primary">Response fields — conversation</Typography>
      <FieldTable rows={GETCONV_CONV} />
      <Typography variant="overline" color="primary">Response fields — per message</Typography>
      <FieldTable rows={GETCONV_MSG} />

      <Divider sx={{ my: 2 }} />

      {/* sendMessage */}
      <EndpointHead verb="POST" path="/conversation/send_message" fn="sendMessage()">{sendChip}</EndpointHead>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: '80ch' }}>
        Replies into a thread or starts a new one. Fully built; {writeOn
          ? <>writes are <b>enabled</b> — sends are live.</>
          : <>inert — outbound is blocked by <code>EBAY_WRITE_ENABLED=false</code>. No message has ever been sent.</>}
      </Typography>
      <Typography variant="overline" color="primary">Request body</Typography>
      <ParamTable rows={SEND_BODY} />

      <Divider sx={{ my: 2 }} />

      {/* updateConversation */}
      <EndpointHead verb="POST" path="/update_conversation" fn="updateConversation()">
        {markReadChip}
      </EndpointHead>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: '80ch' }}>
        Marks a conversation read on eBay's side right after ingest (no reply required first). Historically avoided
        entirely — marking a message read on eBay stops it forwarding to a legacy email system — so this is gated
        behind its own flag, <code>EBAY_MARK_READ_ENABLED</code> ({writeOn ? 'also requires EBAY_WRITE_ENABLED, which is on' : 'also requires EBAY_WRITE_ENABLED, which is currently off'}),
        separate from ordinary sends. <b>1A eBay only</b> by explicit product decision, regardless of this flag or
        AM eBay's own enablement — not extended to AM without a separate decision. eBay's own <code>bulkUpdateConversation</code>
        {' '}(<code>/bulk_update_conversation</code>, up to 10 conversations per call) is not implemented — ingest
        already fetches one conversation's thread at a time, so one call per newly-ingested conversation fits the
        existing pattern without needing to batch.
      </Typography>

      <Divider sx={{ my: 2 }} />

      {/* Derived */}
      <Typography variant="subtitle1" gutterBottom>Derived fields</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: '80ch' }}>
        Things the API doesn&apos;t hand us directly, that we build during ingest (no direct API field).
      </Typography>
      <FieldTable rows={DERIVED} pullLabel="Stored?" />

      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 3 }}>
        Confirmed rows reflect the live channel code (client.js · mapper.js · ingest.js) and migrations 0001 / 0006.
        “To verify” rows pending a raw-response capture or eBay schema confirmation. Browse / Post-Order APIs are out of
        scope here (Message API only). Last reviewed {reviewedLabel}.
      </Typography>
    </Box>
  )
}
